import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BarChart3, ArrowLeft } from 'lucide-react';

const DOCUMENTS = {
  '/privacy': {
    title: 'Privacy Policy',
    sections: [
      ['Data we collect', 'We collect account details, trading account metadata, EA telemetry, trades, and settings needed to provide TrackMyFxEA. We do not request broker passwords.'],
      ['How data is used', 'Your data is used to calculate performance, reports, alerts, account health, and product support. Public sharing is opt-in.'],
      ['Security and retention', 'Data is isolated by user account and protected with authenticated API access. Retention follows your plan and settings.'],
      ['Your choices', 'You can disconnect accounts, delete trading data, request account support, or close your account from the product or by contacting support@trackmyfxea.com.'],
    ],
  },
  '/terms': {
    title: 'Terms of Service',
    sections: [
      ['Service use', 'TrackMyFxEA provides analytics and monitoring tools. You are responsible for the trading accounts, API keys, and data you connect.'],
      ['No trading advice', 'Analytics, scores, AI output, and projections are informational only and are not financial advice or a promise of future performance.'],
      ['Account safety', 'Keep API keys private, use only accounts you control, and notify support if you suspect unauthorized access.'],
      ['Availability', 'We work to keep the service reliable, but scheduled maintenance, provider outages, and market connectivity issues may temporarily affect availability.'],
    ],
  },
  '/refund-policy': {
    title: 'Refund Policy',
    sections: [
      ['Payment review', 'Payments are reviewed against the selected plan and invoice. Contact support before submitting a duplicate payment.'],
      ['Eligible requests', 'Refund requests are reviewed case by case for duplicate charges, failed activation, or a material service issue.'],
      ['How to request help', 'Include your account email, payment reference, plan, and a short description at support@trackmyfxea.com. Never email private keys or passwords.'],
    ],
  },
  '/risk-disclaimer': {
    title: 'Trading Risk Disclaimer',
    sections: [
      ['Market risk', 'Foreign exchange and automated trading involve substantial risk, including the possible loss of all capital.'],
      ['Analytics limits', 'Historical performance, profit factor, drawdown, readiness scores, and simulations cannot predict future results.'],
      ['Your responsibility', 'Test Expert Advisors carefully, use appropriate risk controls, and consult a qualified financial professional for advice about your situation.'],
    ],
  },
  '/contact': {
    title: 'Contact TrackMyFxEA',
    sections: [
      ['Support', 'For login, EA connection, billing, privacy, or account questions, email support@trackmyfxea.com.'],
      ['What to include', 'Tell us the affected page, account number suffix or safe diagnostic details, approximate time, and the steps that reproduce the issue. Do not send passwords or full API keys.'],
    ],
  },
};

export default function LegalPage() {
  const { pathname } = useLocation();
  const document = DOCUMENTS[pathname] || DOCUMENTS['/privacy'];
  return (
    <main className="min-h-screen bg-[#070912] text-gray-100 px-4 py-10 md:py-16">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-10">
          <ArrowLeft size={16} /> Back to TrackMyFxEA
        </Link>
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center">
            <BarChart3 size={20} className="text-white" />
          </div>
          <span className="font-display text-xl font-bold">TrackMyFxEA</span>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold font-display text-white mb-3">{document.title}</h1>
        <p className="text-sm text-gray-500 mb-10">Last updated: July 10, 2026</p>
        <div className="space-y-8">
          {document.sections.map(([heading, body]) => (
            <section key={heading}>
              <h2 className="text-lg font-semibold text-emerald-300 mb-2">{heading}</h2>
              <p className="text-gray-400 leading-7">{body}</p>
            </section>
          ))}
        </div>
        <p className="mt-12 pt-6 border-t border-white/10 text-sm text-gray-500">Questions? <a className="text-emerald-300 hover:text-emerald-200" href="mailto:support@trackmyfxea.com">support@trackmyfxea.com</a></p>
      </div>
    </main>
  );
}
